import { useEffect, useRef } from 'react'
import { useI18n } from '../i18n'
import { OverlayPortal } from './ModalPortal'
import { AlertTriangle } from 'lucide-react'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'

/**
 * In-app confirmation modal — replaces window.confirm()
 * 
 * Props:
 *   open: boolean
 *   title: string
 *   message: string
 *   confirmLabel?: string (default "Continue")
 *   cancelLabel?: string (default "Cancel")
 *   confirmColor?: string (default NAVY)
 *   loading?: boolean — shows spinner on confirm button
 *   icon?: 'warning' | 'info' | null
 *   onConfirm: () => void
 *   onCancel: () => void
 */
export default function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Continue',
  cancelLabel = 'Cancel',
  confirmColor = NAVY,
  loading = false,
  icon = 'warning',
  onConfirm,
  onCancel,
}) {
  const { t } = useI18n()
  const overlayRef = useRef(null)

  // Close on Escape key
  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (e.key === 'Escape' && !loading) onCancel() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, loading, onCancel])

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [open])

  if (!open) return null

  return (
    <OverlayPortal>
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center px-6"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === overlayRef.current && !loading) onCancel() }}
    >
      {/* Animate in */}
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-[340px] overflow-hidden"
        style={{ animation: 'modalSlideUp 0.25s ease-out' }}
      >
        {/* Icon + Title */}
        <div className="pt-6 px-6 text-center">
          {icon === 'warning' && (
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle size={24} className="text-amber-500" />
            </div>
          )}
          <h3 className="text-[16px] font-bold" style={{ color: NAVY }}>{title}</h3>
        </div>

        {/* Message */}
        <div className="px-6 pt-2 pb-5">
          <p className="text-[13px] text-gray-500 text-center leading-relaxed">{message}</p>
        </div>

        {/* Actions */}
        <div className="border-t border-gray-100 flex">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3.5 text-[14px] font-semibold text-gray-400 border-r border-gray-100 active:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-3.5 text-[14px] font-bold flex items-center justify-center gap-2 active:opacity-80 transition-opacity disabled:opacity-70"
            style={{ color: confirmColor }}
          >
            {loading ? (
              <>
                <OrellaSpinner size={16} tone="inherit" />
                {t('common.working', 'Working...')}
              </>
            ) : confirmLabel}
          </button>
        </div>
      </div>

      {/* Keyframe */}
      <style>{`
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(24px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
    </OverlayPortal>
  )
}
